// The toolbar (APPLE-SPEC §5; nav.html in the reference; simplified 15 Oct 2026): one quiet 48 px bar in the bar material, at the top (under
// the strip on Restricted and Confidential screens). Start: the MoET emblem 22 · a 0.5 px hairline · the ECONIC wordmark 18, one link 16 px
// from the edge (the page inset), and nothing beside it (the environment is in About, not on the bar; 21 Oct 2026). Then the
// destinations as plain 13 / 500 text, 28 px apart, the active one in the label colour at 600 with a 2 px navy indicator on the bar's
// bottom edge, a quiet dot where something needs the person. End: the search glyph with its ⌘K hint, the bell (a red dot when something
// needs a decision), the language, the 28 px monogram (Accessibility and display, Help and the session live in its menu). The bar never
// holds a pill, a fill, gold or a name (in Classic; the Signature and Aurora looks add the ministry's name in bronze capitals at ≥ 1180 px
// and a gold-to-navy rule under the bar, looks/*.css). ✦ (the agent) appears only while its inspector is hidden (768–1023, or closed by the person), and
// never where the screen has no ECONIC column (an agent's own pages, which have their own chat box; the conversation screens).
// Below 768 px: the 44 px compact bar — lockup (wordmark 15), search glyph, bell, avatar; the destinations move to the tab bar and the
// language to the account menu. WP-SHELL.
import { Sparkles } from "lucide-react";
import { Link, useLocation } from "react-router";
import { useLocale, useTranslation } from "@ncp/i18n";
import { MINISTRY_NAME, MoetLockup } from "../brand";
import { useDocumentLook } from "../theme";
import { useMediaQuery } from "../../lib/media";
import { useWorkspace } from "../../app/workspace-context";
import { routeFor } from "../../app/routes";
import { ToolbarSearch } from "../search";
import { AccountMenu } from "./AccountMenu";
import { InboxBell } from "./InboxBell";
import { StreakChip } from "../engagement/StreakChip";
import { LanguageToggle } from "./LanguageToggle";
import { PrimaryNav } from "./PrimaryNav";
import { useSidebarState } from "./sidebar-state";

export function TopBar({ search, onOpenAbout }: { search: { open: boolean; onOpenChange: (open: boolean) => void }; onOpenAbout: () => void }) {
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { can, actions } = useWorkspace();
  const sidebar = useSidebarState();
  const { pathname } = useLocation();
  const phone = !useMediaQuery("(min-width: 768px)");
  const wide = useMediaQuery("(min-width: 1180px)");
  const branded = useDocumentLook() !== "classic";
  const contentClearance = can("assistant.use");
  const agentHidden = sidebar.effective === "hidden" && routeFor(pathname)?.sidebar !== "none";
  const agentLabel = t("assistant:econic.shell.sidebar.open");

  return (
    <header className="es-topnav econic-material-bar" role="banner" data-print="hide" data-econic-topbar="">
      <Link to={contentClearance ? "/" : "/trust"} className="es-lockup" aria-label={t("assistant:econic.shell.brand.home", { ministry: locale === "ar" ? MINISTRY_NAME.ar : MINISTRY_NAME.en })}>
        <MoetLockup variant="nav" wordmarkHeight={phone ? 15 : branded ? 20 : 18} height={branded && !phone ? 28 : undefined} brandLine={branded && wide} />
      </Link>
      <PrimaryNav />
      <div className="es-cluster">
        <ToolbarSearch open={search.open} onOpenChange={search.onOpenChange} actions={actions} />
        {agentHidden && contentClearance && !phone ? (
          <button type="button" className="es-icon-btn" onClick={sidebar.open} aria-label={agentLabel} title={agentLabel} data-ask-open="" aria-keyshortcuts="Meta+. Control+.">
            <Sparkles aria-hidden />
          </button>
        ) : null}
        <StreakChip />
        <InboxBell />
        <LanguageToggle className="no-phone" />
        <AccountMenu onOpenAbout={onOpenAbout} />
      </div>
    </header>
  );
}
